import axios from 'axios';
import { useState } from 'react';
import { useCookies } from 'react-cookie';
import { LinkedInCallback } from 'react-linkedin-login-oauth2';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import 'react-simple-flex-grid/lib/main.css';
import useLocalStorage from 'react-use-localstorage';
import './App.scss';
import Avatar from './Components/Avatar';
import AvatarNotFound from './Components/AvatarNotFound';
import ContactUs from './Components/ContactUs';
import CookiesPopup from './Components/CookiesPopup';
import CookiesSettingsPopup from './Components/CookiesSettingsPopup';
import EventAvatars from './Components/EventAvatars';
import Footer from './Components/Footer';
import Gallery from './Components/Gallery';
import GenericNotFound from './Components/GenericNotFound';
import Home from './Components/Home';
import Location from './Components/Location';
import MobileMenu from './Components/MobileMenu';
import Navbar from './Components/Navbar';
import NextEvent from './Components/NextEvent';
import ScrollToTop from './Components/ScrollToTop';
import SignIn from './Components/SignIn';
import Technologies from './Components/Techologies';
import UserProfile from './Components/UserProfile';
import { NodeServer, ProdApiLink } from './config';

function App() {
  const [userName, setUserName] = useLocalStorage('userName', '');
  const [showMenu, setShowMenu] = useState(false);
  const [cookies, setCookie] = useCookies([
    'performance_cookies',
    'targeting_cookies',
    '_ga',
    '_ga_73C9CHYENL',
  ]);
  const [showNavbar, setShowNavbar] = useState(true);
  const [showCookieSettings, setShowCookieSettings] = useState(false);

  const isProd = process.env.REACT_APP_BASE_URL === ProdApiLink;

  const onMenuIconClick = () => {
    setShowMenu(!showMenu);
  };

  const signOut = async () => {
    // send request to sign out
    await axios.get<any>(`${NodeServer.baseURL}${NodeServer.logOut}`, {
      withCredentials: true,
    });
  };

  const onMenuItemClick = async (event: React.MouseEvent<HTMLElement>) => {
    setShowMenu(false);
    const menuId = event.currentTarget.getAttribute('data-id');
    if (menuId === 'SIGN-OUT') {
      setUserName('');
      try {
        await signOut();
      } catch (err) {
        console.error(err);
      }
    }
  };

  let menuItems = [
    {
      id: 'MAIN',
      title: 'Main',
      navLink: '/home',
    },
    {
      id: 'GALLERY',
      title: 'Gallery',
      navLink: '/gallery',
    },
    {
      id: 'TECHNOLOGIES',
      title: 'Technologies',
      navLink: '/technologies',
    },
    {
      id: 'CONTACT-US',
      title: 'Contact us',
      navLink: '/contact-us',
    },
  ];

  if (userName) {
    menuItems = [
      ...menuItems,
      {
        id: 'PROFILE',
        title: 'Profile',
        navLink: '/user-profile',
      },
      {
        id: 'SIGN-OUT',
        title: 'Sign out',
        navLink: '/sign-in',
      },
    ];
  } else {
    menuItems = [
      ...menuItems,
      {
        id: 'SIGN-IN',
        title: 'Sign in',
        navLink: '/sign-in',
      },
    ];
  }

  const acceptAll = () => {
    setCookie('performance_cookies', 'yes', {
      path: '/',
      expires: getExpiryDate(),
      sameSite: true,
      secure: true,
    });
    setCookie('targeting_cookies', 'yes', {
      path: '/',
      expires: getExpiryDate(),
      sameSite: true,
      secure: true,
    });
    window.location.reload();
  };

  const rejectAll = () => {
    setCookie('performance_cookies', 'no', {
      path: '/',
      expires: getExpiryDate(),
      sameSite: true,
      secure: true,
    });
    setCookie('targeting_cookies', 'no', {
      path: '/',
      expires: getExpiryDate(),
      sameSite: true,
      secure: true,
    });
    setCookie('_ga', '', {
      expires: new Date(),
      sameSite: true,
      secure: true,
    });
    setCookie('_ga_73C9CHYENL', '', {
      expires: new Date(),
      sameSite: true,
      secure: true,
    });
    window.location.reload();
  };

  const openCookieSettings = () => {
    setShowCookieSettings(true);
  };

  const getExpiryDate = () => {
    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + 24 * 60 * 60 * 1000);
    return expiryDate;
  };

  const toggleNavbar = (loading: boolean) => {
    setShowNavbar(!loading);
  };

  const confirmMyChoices = (optionsData: {
    performance: boolean;
    targeting: boolean;
  }) => {
    setCookie('performance_cookies', optionsData.performance ? 'yes' : 'no', {
      path: '/',
      expires: getExpiryDate(),
    });
    setCookie('targeting_cookies', optionsData.targeting ? 'yes' : 'no', {
      path: '/',
      expires: getExpiryDate(),
    });
    window.location.reload();
  };

  return (
    <BrowserRouter>
      <ScrollToTop />
      {showNavbar && (
        <Navbar showClose={showMenu} onMenuIconClick={onMenuIconClick} />
      )}
      {!showMenu && (
        <Routes>
          <Route index element={<Home title="Home | FortuneTeller" />} />
          <Route path="home" element={<Home title="Home | FortuneTeller" />} />
          <Route
            path="avatar"
            element={<Avatar title="Avatar | FortuneTeller" />}
          />
          <Route
            path="avatar-not-found"
            element={<AvatarNotFound title="Avatar not found" />}
          />
          <Route
            path="technologies"
            element={<Technologies title="Technologies | FortuneTeller" />}
          />
          <Route
            path="contact-us"
            element={<ContactUs title="Contact us | FortuneTeller" />}
          />
          <Route
            path="user-profile"
            element={<UserProfile title="User profile | FortuneTeller" />}
          />
          <Route
            path="gallery"
            element={<Gallery title="Gallery | FortuneTeller" />}
          >
            <Route
              index
              element={
                <EventAvatars
                  title="Siemens Lab with KPMG | FortuneTeller"
                  id="siemens-lab"
                />
              }
            />
            <Route
              path="siemens-lab"
              element={
                <EventAvatars
                  title="Siemens Lab with KPMG | FortuneTeller"
                  id="siemens-lab"
                />
              }
            />
            <Route
              path="dou-day-picnic"
              element={
                <EventAvatars
                  title="DOU Day Picnic | FortuneTeller"
                  id="dou-day-picnic"
                />
              }
            />
            <Route
              path="general"
              element={
                <EventAvatars
                  title="General | FortuneTeller"
                  id="general"
                />
              }
            />
            <Route
              path="winter-emea-sko"
              element={
                <EventAvatars
                  title="Winter EMEA SKO | FortuneTeller"
                  id="winter-emea-sko"
                />
              }
            />
            <Route
              path="austin-hq"
              element={
                <EventAvatars
                  title="Austin HQ | FortuneTeller"
                  id="austin-hq"
                />
              }
            />
            <Route
              path="warsaw-office-opening"
              element={
                <EventAvatars
                  title="Warsaw Office Opening | FortuneTeller"
                  id="warsaw-office-opening"
                />
              }
            />
            <Route
              path="gartner-it-symposium"
              element={
                <EventAvatars
                  title="Gartner IT Symposium | FortuneTeller"
                  id="gartner-it-symposium"
                />
              }
            />
            <Route
              path="it-arena"
              element={
                <EventAvatars title="IT Arena | FortuneTeller" id="it-arena" />
              }
            />
            <Route
              path="soft-serve-anniversary"
              element={
                <EventAvatars
                  title="SoftServe Anniversary | FortuneTeller"
                  id="soft-serve-anniversary"
                />
              }
            />
            <Route
              path="it-manager"
              element={
                <EventAvatars
                  title="IT Manager | FortuneTeller"
                  id="ItManager"
                />
              }
            />
            <Route
              path="ai-data-expo"
              element={
                <EventAvatars
                  title="AI & Data Expo | FortuneTeller"
                  id="AiDataExpo"
                />
              }
            />
            <Route
              path="code-camp-timisoara"
              element={
                <EventAvatars
                  title="CodeCamp Timisoara | FortuneTeller"
                  id="CodeCampTimisoara"
                />
              }
            />
            <Route
              path="web-summit"
              element={
                <EventAvatars
                  title="Web summit | FortuneTeller"
                  id="WebSummit 2022"
                />
              }
            />
            <Route
              path="money-2020"
              element={
                <EventAvatars
                  title="Money 20/20 | FortuneTeller"
                  id="Money2020"
                />
              }
            />
            {/* <Route
              path="TEST"
              element={<EventAvatars title="TEST | FortuneTeller" id="TEST" />}
            /> */}
            <Route
              path="next-event"
              element={<NextEvent title="Next event | FortuneTeller" />}
            />
          </Route>
          <Route
            path="sign-in"
            element={<SignIn title="Sign in" onProfileLoad={toggleNavbar} />}
          />
          <Route path="linkedin" element={<LinkedInCallback />} />
          <Route
            path="location"
            element={<Location title="Location | FortuneTeller" />}
          />
          <Route
            path="*"
            element={<GenericNotFound title="Not found | FortuneTeller" />}
          />
        </Routes>
      )}
      {showMenu && (
        <MobileMenu menuItems={menuItems} onItemClick={onMenuItemClick} />
      )}
      {isProd && !cookies.performance_cookies && !cookies.targeting_cookies && (
        <CookiesPopup
          onAcceptAll={acceptAll}
          onRejectAll={rejectAll}
          onOpenSettings={openCookieSettings}
        ></CookiesPopup>
      )}

      {showCookieSettings && (
        <CookiesSettingsPopup
          onAcceptAll={acceptAll}
          onConfirmMyChoices={confirmMyChoices}
        ></CookiesSettingsPopup>
      )}
      <Footer></Footer>
    </BrowserRouter>
  );
}

export default App;
